import { deleteConfirmModal } from "@helpers/DeleteValidate";
import { errorHandler } from "@helpers/errorHandler";
import { Call, CallWithFormDataFile, downloadBlob } from "@helpers/fetch";
import {
    startUILoading,
    stopUILoading,
} from "@redux/actions/administrador/ui/ui";
import { createSlice } from "@reduxjs/toolkit";
import Swal from "sweetalert2";

const initialState = {
    active: null,
    performanceReport: null,
    missingReport: null,
    fields: [],
    performance: [],
};

export const TeacherFollowUp = createSlice({
    name: "TeacherFollowUp",
    initialState,
    reducers: {
        setActive: (state, action) => {
            state.active = action.payload;
        },
        clearActive: (state) => {
            state.active = initialState.active;
        },
        setPerformanceReport: (state, action) => {
            state.performanceReport = action.payload;
        },
        clearPerformanceReport: (state) => {
            state.performanceReport = initialState.performanceReport;
        },
        setMissingReport: (state, action) => {
            state.missingReport = action.payload;
        },
        clearMissingReport: (state) => {
            state.missingReport = initialState.missingReport;
        },
        setFields: (state, action) => {
            state.fields = action.payload;
        },
        clearFields: (state) => {
            state.fields = initialState.fields;
        },
        setPerformance: (state, action) => {
            state.performance = action.payload;
        },
        clearPerformance: (state) => {
            state.performance = initialState.performance;
        },
        clearState: (state) => {
            state = initialState;
        },
    },
});

export const {
    setActive,
    clearActive,
    setPerformanceReport,
    clearPerformanceReport,
    setMissingReport,
    clearMissingReport,
    setFields,
    clearFields,
    setPerformance,
    clearPerformance,
    clearState,
} = TeacherFollowUp.actions;

export default TeacherFollowUp.reducer;

export const startCreate = (
    values,
    setErrors = () => {},
    callback = () => {}
) => {
    return async (dispatch) => {
        dispatch(startUILoading());

        const resp = await Call(
            `operators/schedules/follow-up`,
            "POST",
            values
        );

        if (resp.success) {
            callback();
            Swal.fire({
                title: "Éxito",
                text: "Seguimiento creado.",
                icon: "success",
                toast: true,
                position: "bottom-end",
                timer: 4000,
            });
        } else {
            errorHandler(resp, setErrors);
        }

        dispatch(stopUILoading());
    };
};

export const startUpdate = (
    id,
    values,
    setErrors = () => {},
    callback = () => {}
) => {
    return async (dispatch) => {
        dispatch(startUILoading());

        const resp = await Call(
            `operators/schedules/follow-up/${id}`,
            "PATCH",
            values
        );

        if (resp.success) {
            callback();
            Swal.fire({
                title: "Éxito",
                text: "Seguimiento actualizado.",
                icon: "success",
                toast: true,
                position: "bottom-end",
                timer: 4000,
            });
        } else {
            errorHandler(resp, setErrors);
        }

        dispatch(stopUILoading());
    };
};

export const startDelete = (id, callback = () => {}) => {
    return async (dispatch) => {
        deleteConfirmModal(async () => {
            dispatch(startUILoading());

            const resp = await Call(
                `operators/schedules/follow-up/${id}`,
                "DELETE"
            );

            if (resp.success) {
                callback();
                Swal.fire({
                    title: "Éxito",
                    text: "Seguimiento eliminado.",
                    icon: "success",
                    toast: true,
                    position: "bottom-end",
                    timer: 4000,
                });
            } else {
                errorHandler(resp);
            }

            dispatch(stopUILoading());
        });
    };
};

export const startGetTeachersPerformanceReport = (
    values,
    is_export = false
) => {
    return async (dispatch) => {
        dispatch(startUILoading());

        const { cycles, coordination_ids, curriculum_ids } = values;

        const params = new URLSearchParams();
        params.set("cycles", `${cycles.year}-${cycles.sub_cycle}`);
        if (coordination_ids.length > 0) {
            params.set(
                "coordination_ids",
                coordination_ids.map((c) => c.value).join(",")
            );
        }
        if (curriculum_ids.length > 0) {
            params.set(
                "curriculum_ids",
                curriculum_ids.map((c) => c.value).join(",")
            );
        }
        if (is_export) {
            params.set("is_export", 1);
        }

        const url = "teachers/follow-up/performance_report";

        if (is_export) {
            const resp = await CallWithFormDataFile(
                url,
                "GET",
                params.toString()
            );
            downloadBlob(
                resp,
                `Reporte de desempeño de docentes ${cycles.year}-${cycles.sub_cycle}`
            );
        } else {
            const resp = await Call(url, "GET", params.toString());
            if (resp.success) {
                dispatch(setPerformanceReport(resp.success));
            }
        }

        dispatch(stopUILoading());
    };
};

export const startGetTeachersMissingReport = (values, is_export = false) => {
    return async (dispatch) => {
        dispatch(startUILoading());

        const { cycles, coordination_ids, curriculum_ids } = values;

        const params = new URLSearchParams();
        params.set("cycles", `${cycles.year}-${cycles.sub_cycle}`);
        if (coordination_ids.length > 0) {
            params.set(
                "coordination_ids",
                coordination_ids.map((c) => c.value).join(",")
            );
        }
        if (curriculum_ids.length > 0) {
            params.set(
                "curriculum_ids",
                curriculum_ids.map((c) => c.value).join(",")
            );
        }
        if (is_export) {
            params.set("is_export", 1);
        }

        const url = "teachers/follow-up/missing_report";

        if (is_export) {
            const resp = await CallWithFormDataFile(
                url,
                "GET",
                params.toString()
            );
            downloadBlob(
                resp,
                `Reporte de docentes faltantes de seguimiento ${cycles.year}-${cycles.sub_cycle}`
            );
        } else {
            const resp = await Call(url, "GET", params.toString());
            if (resp.success) {
                dispatch(setMissingReport(resp.success));
            }
        }

        dispatch(stopUILoading());
    };
};

export const startSaveFields = (
    values,
    setErrors = () => {},
    callback = () => {}
) => {
    return async (dispatch) => {
        dispatch(startUILoading());

        const resp = await Call(
            `operators/reports/follow-up/fields`,
            "POST",
            values
        );

        if (resp.success) {
            callback();
            dispatch(setFields(resp.success));
            Swal.fire({
                title: "Éxito",
                text: "Campos del formulario configurados.",
                icon: "success",
                toast: true,
                position: "bottom-end",
                timer: 4000,
            });
        } else {
            errorHandler(resp, setErrors);
        }

        dispatch(stopUILoading());
    };
};

export const startGetFields = (setErrors = () => {}, callback = () => {}) => {
    return async (dispatch) => {
        dispatch(startUILoading());

        const resp = await Call(`operators/reports/follow-up/fields`, "GET");

        if (resp.success) {
            callback();
            dispatch(setFields(resp.success));
        } else {
            errorHandler(resp, setErrors);
        }

        dispatch(stopUILoading());
    };
};

export const startSavePerformance = (
    values,
    setErrors = () => {},
    callback = () => {}
) => {
    return async (dispatch) => {
        dispatch(startUILoading());

        const resp = await Call(
            `operators/reports/follow-up/performance`,
            "POST",
            values
        );

        if (resp.success) {
            callback();
            Swal.fire({
                title: "Éxito",
                text: "Configuración de criterios de desempeño guardada.",
                icon: "success",
                toast: true,
                position: "bottom-end",
                timer: 4000,
            });
        } else {
            errorHandler(resp, setErrors);
        }

        dispatch(stopUILoading());
    };
};

export const startGetPerformance = (setErrors = () => {}, callback = () => {}) => {
    return async (dispatch) => {
        dispatch(startUILoading());

        const resp = await Call(`operators/reports/follow-up/performance`, "GET");

        if (resp.success) {
            callback();
            dispatch(setPerformance(resp.success));
        } else {
            errorHandler(resp, setErrors);
        }

        dispatch(stopUILoading());
    };
};
